import React, { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Footer from '../components/Footer';
import HealthcareChat from '../components/HealthcareChat';
import AdvancedChat from '../components/AdvancedChat';
import OrchestratorUpload from '../components/OrchestratorUpload';
import DemoDisclaimer from '../components/DemoDisclaimer';
import './DemoPage.css';

interface DemoConfig {
  title: string;
  subtitle: string;
  description: string;
  architecture: string[];
  diagramUrl: string;
  examples: string[];
  status: 'live' | 'coming-soon';
}

const demoConfigs: Record<string, DemoConfig> = {
  'rag-simple': {
    title: 'RAG Simple',
    subtitle: 'Healthcare Compliance Intelligence',
    description: 'A basic Retrieval-Augmented Generation system for querying hospital policy documents. Ask questions about HealthFirst Medical Center\'s policies on HIPAA, patient rights, medication administration, and more.',
    architecture: [
      'Question',
      'Embed Query',
      'Vector Search',
      'Top-K Retrieval',
      'LLM Generation',
      'Answer + Sources',
    ],
    diagramUrl: '/rag-simple-diagram.png',
    examples: [
      'What is the policy for medication administration?',
      'How long must medical records be retained?',
      'What are the requirements for patient informed consent?',
      'What training is required for new employees?',
      'How should HIPAA breaches be reported?',
    ],
    status: 'live',
  },
  'rag-advanced': {
    title: 'RAG Advanced',
    subtitle: 'Cross-Domain Compliance Analysis',
    description: 'An enhanced RAG system that cross-references hospital policies against federal regulations (HIPAA, CMS, FDA) to identify compliance gaps. Uses query rewriting, hybrid search, and gap analysis.',
    architecture: [
      'Question',
      'Query Rewrite',
      'Hybrid Search',
      'Gap Analysis',
      'LLM Generation',
      'Answer + Gaps',
    ],
    diagramUrl: '/rag-advanced-diagram.png',
    examples: [
      'Is our medical records retention policy HIPAA compliant?',
      'Compare our patient rights policies against CMS requirements',
      'What gaps exist in our HIPAA security controls?',
      'Are our informed consent procedures compliant with federal regulations?',
    ],
    status: 'live',
  },
  'orchestrator': {
    title: 'Orchestrator Agent',
    subtitle: 'Document Compliance Review',
    description: 'A single orchestrator agent that uses multiple tools to analyze uploaded documents against federal healthcare regulations. Upload a policy, procedure, or agreement to see agent reasoning and tool usage in action.',
    architecture: [
      'Parse (tool)',
      'Classify (agent)',
      'Retrieve (RAG)',
      'Analyze (agent)',
      'Report (tool)',
    ],
    diagramUrl: '/orchestrator-diagram.png',
    examples: [
      'Upload a HIPAA policy for compliance review',
      'Upload a Business Associate Agreement',
      'Upload a medical device procedure',
    ],
    status: 'live',
  },
};

const DemoPage: React.FC = () => {
  const { demoId } = useParams<{ demoId: string }>();
  const config = demoId ? demoConfigs[demoId] : null;
  const chatRef = useRef<{ sendMessage: (msg: string) => void } | null>(null);

  if (!config) {
    return (
      <>
        <div className="demo-page">
          <div className="demo-container">
            <h1>Demo not found</h1>
            <Link to="/demos" className="back-link">← Back to Demos</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const isLive = config.status === 'live';
  const isOrchestrator = demoId === 'orchestrator';
  const isRagSimple = demoId === 'rag-simple';
  const isRagAdvanced = demoId === 'rag-advanced';

  const handleExampleClick = (example: string) => {
    if (chatRef.current) {
      chatRef.current.sendMessage(example);
    }
    // For chat inputs, find the appropriate textarea
    const inputSelector = isRagAdvanced 
      ? '.advanced-chat-input textarea' 
      : '.healthcare-chat-input textarea';
    const input = document.querySelector(inputSelector) as HTMLTextAreaElement;
    if (input) {
      input.value = example;
      input.focus();
      // Trigger React's onChange
      const event = new Event('input', { bubbles: true });
      input.dispatchEvent(event);
    }
  };

  return (
    <>
      <div className="demo-page">
        <div className="demo-container">
          <Link to="/demos" className="back-link">← Back to Demos</Link>

          <header className="demo-header">
            <div className="demo-title-row">
              <h1>{config.title}</h1>
              <span className={`status-badge ${config.status}`}>
                {isLive ? '● Live' : 'Coming Soon'}
              </span>
            </div>
            <h2 className="demo-subtitle">{config.subtitle}</h2>
            <p className="demo-description">{config.description}</p>
          </header>

          <section className="demo-architecture">
            <h3>Architecture</h3>
            <div className="architecture-diagram">
              <img 
                src={config.diagramUrl} 
                alt={`${config.title} Architecture Diagram`}
                className="diagram-image"
              />
            </div>
          </section>

          <section className="demo-interactive">
            {isLive && <DemoDisclaimer />}
            {isRagSimple && isLive ? (
              <HealthcareChat />
            ) : isRagAdvanced && isLive ? (
              <AdvancedChat />
            ) : isOrchestrator && isLive ? (
              <OrchestratorUpload />
            ) : (
              <div className="chat-placeholder">
                <div className="chat-placeholder-content">
                  <span className="chat-icon"></span>
                  <p>This demo is under development</p>
                  <span>Check back soon!</span>
                </div>
              </div>
            )}
          </section>

          <section className="demo-examples">
            <h3>Example {isOrchestrator ? 'Use Cases' : 'Questions'}</h3>
            <div className="examples-list">
              {config.examples.map((example, index) => (
                <button
                  key={index}
                  className="example-button"
                  onClick={() => isRagSimple && isLive && handleExampleClick(example)}
                  disabled={!isLive}
                >
                  {example}
                </button>
              ))}
            </div>
          </section>

          {isRagSimple && (
            <section className="demo-info">
              <h3>About This Demo</h3>
              <div className="info-grid">
                <div className="info-card">
                  <h4>Knowledge Base</h4>
                  <p>10 hospital policy documents covering HIPAA, patient rights, medications, quality improvement, and more.</p>
                </div>
                <div className="info-card">
                  <h4>Embeddings</h4>
                  <p>318 chunks embedded using all-MiniLM-L6-v2 (384 dimensions).</p>
                </div>
                <div className="info-card">
                  <h4>LLM</h4>
                  <p>Llama 3.1 8B via Groq for fast inference.</p>
                </div>
                <div className="info-card">
                  <h4>Retrieval</h4>
                  <p>Cosine similarity search returning top 3 most relevant chunks.</p>
                </div>
              </div>
            </section>
          )}

          {isRagAdvanced && (
            <section className="demo-info">
              <h3>About This Demo</h3>
              <div className="info-grid">
                <div className="info-card">
                  <h4>Dual Knowledge Bases</h4>
                  <p>Hospital policies (318 chunks) + Federal regulations HIPAA/CMS/FDA (49 chunks) for cross-domain analysis.</p>
                </div>
                <div className="info-card">
                  <h4>Hybrid Search</h4>
                  <p>Combines dense embeddings (70%) with BM25 keyword matching (30%) for better retrieval.</p>
                </div>
                <div className="info-card">
                  <h4>Query Rewriting</h4>
                  <p>LLM enhances queries with compliance terminology before retrieval.</p>
                </div>
                <div className="info-card">
                  <h4>Gap Analysis</h4>
                  <p>Automatically identifies compliance gaps between hospital policies and federal requirements.</p>
                </div>
              </div>
            </section>
          )}

          {isOrchestrator && (
            <section className="demo-info">
              <h3>About This Demo</h3>
              <div className="info-grid">
                <div className="info-card">
                  <h4>Single Agent Architecture</h4>
                  <p>One orchestrator agent coordinates multiple tools rather than fake "multi-agent" chains.</p>
                </div>
                <div className="info-card">
                  <h4>Tool vs Agent</h4>
                  <p>Tools (parse, retrieve, format) are deterministic. Agent steps (classify, analyze) involve LLM reasoning.</p>
                </div>
                <div className="info-card">
                  <h4>RAG Integration</h4>
                  <p>Uses the same federal regulations knowledge base to compare uploaded documents against requirements.</p>
                </div>
                <div className="info-card">
                  <h4>Conditional Routing</h4>
                  <p>Classification output determines which regulations to retrieve - actual orchestration logic.</p>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DemoPage;
